import { NavLink, Link } from "react-router-dom";
import { Home, Zap, Clapperboard, Music, Radio, MessageCircle, Bell, User, Apple, Info, ShieldCheck, Video, History, Wallet } from "lucide-react";
import { APP_STORE_URL } from "@/constants/site";
import { PrivacyOptionsLink } from "../ads";
import { useAuth } from "../auth";

const ITEMS = [
  { to: "/app", end: true, icon: Home, label: "Home", testId: "nav-home" },
  { to: "/app/shorts", icon: Zap, label: "Shorts", testId: "nav-shorts" },
  { to: "/app/series", icon: Clapperboard, label: "Series", testId: "nav-series" },
  { to: "/app/music", icon: Music, label: "Music", testId: "nav-music" },
  { to: "/app/live", icon: Radio, label: "Live", testId: "nav-live" },
  { to: "/app/go-live", icon: Video, label: "Go Live", testId: "nav-go-live" },
  { to: "/app/past-live", icon: History, label: "Past Live", testId: "nav-past-live" },
  { to: "/app/messages", icon: MessageCircle, label: "Messages", testId: "nav-messages" },
  { to: "/app/notifications", icon: Bell, label: "Notifications", testId: "nav-notifications" },
  { to: "/app/wallet", icon: Wallet, label: "Wallet", testId: "nav-wallet" },
  { to: "/app/profile", icon: User, label: "My Profile", testId: "nav-profile" },
];

const Sidebar = () => {
  const { user } = useAuth();
  const isAdmin = user && (user.isAdmin || user.isSuperAdmin);
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-20 flex-col border-r border-white/10 bg-obsidian lg:flex" data-testid="web-sidebar">
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
        {ITEMS.map(({ to, end, icon: Icon, label, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1.5 rounded-xl px-1 py-3 text-center text-[9px] font-medium leading-tight transition-colors duration-200 ${
                isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/app/admin"
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex flex-col items-center gap-1.5 rounded-xl px-1 py-3 text-center text-[9px] font-medium leading-tight transition-colors duration-200 ${
                isActive ? "bg-crimson/20 text-crimson" : "text-crimson/70 hover:bg-crimson/10 hover:text-crimson"
              }`
            }
          >
            <ShieldCheck size={20} />
            Admin
          </NavLink>
        )}
      </nav>
    <div className="flex flex-col items-center gap-1 border-t border-white/10 py-3">
      <Link to="/about" title="About the app" data-testid="sidebar-about-link" className="rounded-xl p-2.5 text-slate-500 transition-colors hover:bg-white/5 hover:text-white">
        <Info size={17} />
      </Link>
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" title="Get the iOS app" data-testid="sidebar-app-store-link" className="rounded-xl p-2.5 text-slate-500 transition-colors hover:bg-white/5 hover:text-white">
        <Apple size={17} />
      </a>
      <Link to="/privacy-policy" title="Privacy Policy" data-testid="sidebar-privacy-link" className="rounded-xl p-2.5 text-slate-500 transition-colors hover:bg-white/5 hover:text-white">
        <ShieldCheck size={17} />
      </Link>
      <PrivacyOptionsLink compact />
    </div>
    </aside>
  );
};

const MOBILE_ITEMS = [
  { to: "/app", end: true, icon: Home, label: "Home", testId: "mnav-home" },
  { to: "/app/shorts", icon: Zap, label: "Shorts", testId: "mnav-shorts" },
  { to: "/app/live", icon: Radio, label: "Live", testId: "mnav-live" },
  { to: "/app/messages", icon: MessageCircle, label: "Chat", testId: "mnav-messages" },
  { to: "/app/profile", icon: User, label: "Profile", testId: "mnav-profile" },
];

export const MobileNav = () => (
  <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-white/10 bg-obsidian/95 backdrop-blur-xl lg:hidden" data-testid="web-mobile-nav">
    {MOBILE_ITEMS.map(({ to, end, icon: Icon, label, testId }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        data-testid={testId}
        className={({ isActive }) =>
          `flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${isActive ? "text-crimson" : "text-slate-500"}`
        }
      >
        <Icon size={20} />
        {label}
      </NavLink>
    ))}
  </nav>
);

export default Sidebar;
